import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api, { BACKEND_URL } from "@/lib/api";
import { getMusicUrl, getCoverUrl, getPlaylistCoverUrl, getProfilePhotoUrl, getBannerUrl, getDefaultAvatar, getDefaultBanner, optimizeImage } from "@/lib/mediaUrls";
import { getDisplayPlays, formatDisplayPlays } from "@/lib/promoPlays";
import { PlayButton } from "@/components/player/PlayButton";
import { SinglesLockBadge, isSinglesForSale } from "@/components/singles/SinglesLockBadge";
import CollaboratorChips from "@/components/CollaboratorChips";
import FrequentCollaborators from "@/components/FrequentCollaborators";
import ProfileActionsMenu from "@/components/ProfileActionsMenu";
import InviteToStudioButton from "@/components/studio/InviteToStudioButton";
import AudioPlayer from "@/components/AudioPlayer";
import SharedNavigation from "@/components/SharedNavigation";
import { BadgeWithModal } from "@/components/BadgeWithModal";
import MembershipBadges from "@/components/MembershipBadges";
import PresenceStatusBadge from "@/components/PresenceStatusBadge";
import PremiumBenefitsCard from "@/components/PremiumBenefitsCard";
import { evolveBadges } from "@/lib/badges";
import { MetricInfoIcon } from "@/components/MetricTooltip";
import GiftPicker from "@/components/GiftPicker";
import TopSupportersModal from "@/components/TopSupportersModal";
import SupportersPreviewRow from "@/components/SupportersPreviewRow";
import BeatCard from "@/components/BeatCard";
import ContactProducerDialog from "@/components/ContactProducerDialog";
import AchievementsTab from "@/components/AchievementsTab";
import OfficialHubRails from "@/components/OfficialHubRails";
import PresenceDot from "@/components/PresenceDot";
import TrackActionsMenu from "@/components/TrackActionsMenu";
import ShareToCommunityDialog from "@/components/ShareToCommunityDialog";
import ProfileContestWins from "@/components/contests/ProfileContestWins";
import ShowMoreGrid from "@/components/ShowMoreGrid";
import { toast } from "sonner";

// Social Link Component
const SocialLink = ({ platform, url, icon }) => {
  if (!url) return null;
  
  const getFullUrl = () => {
    if (url.startsWith('http')) return url;
    const prefixes = {
      instagram: 'https://instagram.com/',
      tiktok: 'https://tiktok.com/@',
      youtube: 'https://youtube.com/',
      spotify: 'https://open.spotify.com/artist/',
      apple_music: 'https://music.apple.com/artist/',
      website: ''
    };
    return prefixes[platform] ? prefixes[platform] + url : url;
  };

  const icons = {
    instagram: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
    ),
    tiktok: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
      </svg>
    ),
    youtube: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
    spotify: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
      </svg>
    ),
    apple_music: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M23.994 6.124a9.23 9.23 0 00-.24-2.19c-.317-1.31-1.062-2.31-2.18-3.043a5.022 5.022 0 00-1.877-.726 10.496 10.496 0 00-1.564-.15c-.04-.003-.083-.01-.124-.013H5.986c-.152.01-.303.017-.455.026-.747.043-1.49.123-2.193.401-1.336.53-2.3 1.452-2.865 2.78-.192.448-.292.925-.363 1.408-.056.392-.088.785-.1 1.18 0 .032-.007.062-.01.093v12.223c.01.14.017.283.027.424.05.815.154 1.624.497 2.373.65 1.42 1.738 2.353 3.234 2.801.42.127.856.187 1.293.228.555.053 1.11.06 1.667.06h11.03a12.5 12.5 0 001.57-.1c.822-.106 1.596-.35 2.295-.81a5.046 5.046 0 001.88-2.207c.186-.42.293-.87.37-1.324.113-.675.138-1.358.137-2.04-.002-3.8 0-7.595-.003-11.393zm-6.423 3.99v5.712c0 .417-.058.827-.244 1.206-.29.59-.76.962-1.388 1.14-.35.1-.706.157-1.07.173-.95.042-1.8-.364-2.22-1.163-.414-.786-.378-1.68.206-2.467.39-.525.93-.836 1.574-.97.376-.077.763-.1 1.145-.14.397-.038.79-.09 1.17-.21.193-.06.282-.17.287-.384V9.235c0-.09-.018-.136-.12-.148-.498-.06-.994-.132-1.49-.2l-3.503-.478c-.156-.02-.312-.044-.468-.06-.098-.01-.144.034-.153.13-.003.036 0 .073 0 .11v7.66c0 .376-.04.746-.18 1.1-.24.61-.674 1.04-1.285 1.28-.396.154-.81.22-1.233.24-.89.04-1.678-.243-2.193-.97-.678-.954-.596-2.313.37-3.136.514-.437 1.13-.63 1.803-.7.426-.044.856-.05 1.28-.1.306-.038.6-.11.87-.26.14-.078.2-.18.2-.34V5.02c0-.093.013-.14.112-.155l6.423-.988c.105-.017.21-.04.314-.042.078 0 .107.038.107.114v6.166z"/>
      </svg>
    ),
    website: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/>
      </svg>
    )
  };

  return (
    <a
      href={getFullUrl()}
      target="_blank"
      rel="noopener noreferrer"
      className="w-10 h-10 rounded-full bg-gray-800 hover:bg-purple-600 flex items-center justify-center text-gray-400 hover:text-white transition-all"
      title={platform.charAt(0).toUpperCase() + platform.slice(1).replace('_', ' ')}
    >
      {icons[platform]}
    </a>
  );
};

const ArtistProfilePage = () => {
  const { user_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [followLoading, setFollowLoading] = useState(false);
  const [playingTrackId, setPlayingTrackId] = useState(null);
  // True only when the player advanced to this track via the queue
  // (so AudioPlayer auto-plays). Cleared when user manually clicks Play.
  const [autoPlayQueue, setAutoPlayQueue] = useState(false);
  // User's own playlists (for the Add to Playlist menu on track cards).
  const [myPlaylists, setMyPlaylists] = useState([]);
  // Which track's Add-to-Playlist menu is open
  const [addToPlMenuFor, setAddToPlMenuFor] = useState(null);
  const [suggestedArtists, setSuggestedArtists] = useState([]);
  // Fix 1 (Feb 2026) — Tracks tab pagination (Load more).
  const [loadingMoreTracks, setLoadingMoreTracks] = useState(false);
  const [tracksExhausted, setTracksExhausted] = useState(false);
  // Phase 7.1 Milestone B — public earnings stats for profile chips
  const [earningsStats, setEarningsStats] = useState(null);
  // Phase 7.1 Milestone C — top supporters modal + most-supported track
  const [supportersOpen, setSupportersOpen] = useState(false);
  const [supportersRefreshKey, setSupportersRefreshKey] = useState(0);
  const [mostSupportedTrack, setMostSupportedTrack] = useState(null);

  // Phase 3.2: Profile tabs state
  const [activeTab, setActiveTab] = useState('tracks'); // 'tracks' | 'features' | 'playlists' | 'spotlight' | 'achievements' | 'rgm-studio' (only on the canonical RGM Studio Official account)
  const [features, setFeatures] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [beats, setBeats] = useState([]);
  const [businesses, setBusinesses] = useState([]); // Phase 20.1
  const [contactBeat, setContactBeat] = useState(null);
  // Phase 16.0 — Share-to-community + track actions menu state (used by the
  // per-track ⋯ menu so creators can manage their tracks directly from the
  // profile rows instead of having to open each track detail page).
  const [shareCommunityTrack, setShareCommunityTrack] = useState(null);
  const [spotlightRank, setSpotlightRank] = useState(null);
  const [crews, setCrews] = useState({ created: [], member_of: [], clubs: { owned: [], subscribed: [] } });
  const [tabsLoaded, setTabsLoaded] = useState({ features: false, playlists: false, spotlight: false, crews: false, beats: false, business: false });

  // RGM Studio Official tab (canonical-identity architecture, Feb 2026).
  // The tab appears ONLY when the loaded artist profile IS the
  // runtime-resolved official account (``users.is_official == true``).
  // Identity is derived from the existing public
  // ``GET /api/official/profile`` resolver — no backend changes, no
  // hardcoded user_id, no new DB field. Ordinary artist profiles set
  // ``isOfficialProfile`` to false and the tab never appears.
  const [officialData, setOfficialData] = useState(null);
  const [isOfficialProfile, setIsOfficialProfile] = useState(false);
  
  // Followers/Following modal state
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [followersModalTab, setFollowersModalTab] = useState('followers'); // 'followers' or 'following'
  const [followersList, setFollowersList] = useState([]);
  const [followingList, setFollowingList] = useState([]);
  const [loadingFollowers, setLoadingFollowers] = useState(false);

  useEffect(() => {
    fetchProfile();
    checkCurrentUser();
    fetchSuggestedArtists();
    fetchEarningsStats();
    fetchMostSupportedTrack();
    // Phase 15.6P — auto-load the "Featured On" tab data on profile mount
    // so the tab label shows the actual count (e.g., "🎤 Featured On (3)")
    // instead of the count being hidden until the tab is clicked. This
    // makes collaboration visibility immediate on every artist profile.
    loadTabData('features');
  }, [user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // RGM Studio Official tab detection — Feb 2026.
  // On every artist profile mount we consult the public resolver
  // ``GET /api/official/profile`` (already used by /rgm-studio) and
  // set ``isOfficialProfile`` iff ``official_active`` is true AND
  // ``account.user_id`` equals the currently viewed profile.
  // No backend changes, no new endpoint, no hardcoded user_id.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/official/profile');
        if (cancelled) return;
        const acc = r.data?.account || null;
        const active = !!r.data?.official_active && !!acc?.user_id;
        if (active && acc.user_id === user_id) {
          setOfficialData(r.data);
          setIsOfficialProfile(true);
        } else {
          setOfficialData(null);
          setIsOfficialProfile(false);
        }
      } catch {
        // Silent — the tab simply won't appear if the resolver
        // errors. Ordinary artist profiles remain unaffected.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user_id]);

  // Phase 15.5 P1: Deep-link tab selection via ?tab=beats|features|playlists|spotlight|crews|achievements
  // Ref-guard against StrictMode double-invoke + dep re-fire causing duplicate fetches.
  const deepLinkAppliedRef = useRef(false);
  useEffect(() => {
    const tab = (searchParams.get('tab') || '').toLowerCase();
    const valid = ['tracks', 'features', 'playlists', 'spotlight', 'crews', 'beats', 'achievements', 'business'];
    if (!tab || !valid.includes(tab)) {
      deepLinkAppliedRef.current = false;
      return;
    }
    if (deepLinkAppliedRef.current) return;
    deepLinkAppliedRef.current = true;
    if (tab !== activeTab) {
      setActiveTab(tab);
      if (tab !== 'tracks' && !tabsLoaded[tab]) {
        loadTabData(tab);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user_id]);

  const fetchEarningsStats = async () => {
    try {
      const r = await api.get(`/earnings/artist/${user_id}/public-stats`);
      setEarningsStats(r.data);
    } catch (_e) {
      setEarningsStats(null);
    }
  };

  const fetchMostSupportedTrack = async () => {
    try {
      const r = await api.get(`/top-gifters/artist/${user_id}/most-supported-track`);
      setMostSupportedTrack(r.data?.track || null);
    } catch (_e) {
      setMostSupportedTrack(null);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await api.get(`/artist/${user_id}`);
      setProfileData(response.data);
      setFollowerCount(response.data.user?.followers?.length || 0);
    } catch (error) {
      console.error("Failed to fetch profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
      // Check if following this artist
      if (response.data?.user_id) {
        checkFollowStatus(response.data.user_id);
        fetchMyPlaylists();
      }
    } catch (error) {
      // Not logged in - that's okay for public profile
      setCurrentUser(null);
    }
  };

  const fetchMyPlaylists = async () => {
    try {
      const r = await api.get('/playlists/my');
      setMyPlaylists(r.data?.playlists || []);
    } catch (_e) {
      setMyPlaylists([]);
    }
  };

  const handleAddToPlaylist = async (playlistId, submissionId) => {
    try {
      await api.post(`/playlists/${playlistId}/tracks/${submissionId}`);
      toast.success('Added to playlist');
      setAddToPlMenuFor(null);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (typeof msg === 'string' && msg.toLowerCase().includes('already')) {
        toast.info('Already in this playlist');
      } else {
        toast.error('Failed to add to playlist');
      }
    }
  };

  // 🎶 Profile autoplay queue — when a track ends, advance to the NEXT track
  // in the (sorted) submissions list. Pinned tracks come first by backend
  // sort order; unpinned follow newest-first. Stops at end of list.
  const advanceProfileQueue = (currentSid) => {
    const list = profileData?.submissions || [];
    const idx = list.findIndex((t) => t.submission_id === currentSid);
    if (idx === -1 || idx >= list.length - 1) {
      setPlayingTrackId(null);
      setAutoPlayQueue(false);
      return;
    }
    const next = list[idx + 1];
    if (next) {
      setPlayingTrackId(next.submission_id);
      setAutoPlayQueue(true);
    }
  };

  const checkFollowStatus = async (followerId) => {
    try {
      const response = await api.get(`/artist/${user_id}/follow-status?follower_id=${followerId}`);
      setIsFollowing(response.data.is_following);
      setFollowerCount(response.data.follower_count);
    } catch (error) {
      console.error("Failed to check follow status:", error);
    }
  };

  const fetchSuggestedArtists = async () => {
    try {
      const response = await api.get(`/discover/suggested/${user_id}`);
      setSuggestedArtists(response.data.artists || []);
    } catch (error) {
      console.error("Failed to fetch suggested artists:", error);
    }
  };

  const fetchFollowersData = async (tab) => {
    setLoadingFollowers(true);
    try {
      if (tab === 'followers') {
        const response = await api.get(`/artist/${user_id}/followers`);
        setFollowersList(response.data.followers || []);
      } else {
        const response = await api.get(`/artist/${user_id}/following`);
        setFollowingList(response.data.following || []);
      }
    } catch (error) {
      console.error(`Failed to fetch ${tab}:`, error);
    } finally {
      setLoadingFollowers(false);
    }
  };

  const openFollowersModal = (tab) => {
    setFollowersModalTab(tab);
    setShowFollowersModal(true);
    fetchFollowersData(tab);
  };

  const handleFollow = async () => {
    if (!currentUser) {
      window.location.href = '/login';
      return;
    }

    setFollowLoading(true);
    try {
      if (isFollowing) {
        await api.delete(`/artist/${user_id}/follow?follower_id=${currentUser.user_id}`);
        setIsFollowing(false);
        setFollowerCount(prev => Math.max(0, prev - 1));
      } else {
        await api.post(`/artist/${user_id}/follow`, { follower_id: currentUser.user_id });
        setIsFollowing(true);
        setFollowerCount(prev => prev + 1);
      }
    } catch (error) {
      console.error("Failed to follow/unfollow:", error);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleLoadMoreTracks = useCallback(async () => {
    if (loadingMoreTracks || tracksExhausted) return;
    const list = profileData?.submissions || [];
    if (list.length === 0) return;
    const last = list[list.length - 1];
    const cursor = last?.created_at;
    if (!cursor) { setTracksExhausted(true); return; }
    setLoadingMoreTracks(true);
    try {
      const r = await api.get(`/artist/${user_id}/tracks`, {
        params: { after: cursor, limit: 48 },
      });
      const newItems = r.data?.items || [];
      const existingIds = new Set(list.map((t) => t.submission_id));
      const merged = list.concat(newItems.filter((t) => !existingIds.has(t.submission_id)));
      setProfileData((prev) => (prev ? { ...prev, submissions: merged, has_more_tracks: !!r.data?.has_more } : prev));
      if (!r.data?.has_more || newItems.length === 0) {
        setTracksExhausted(true);
      }
    } catch (err) {
      console.error("Failed to load more tracks:", err);
    } finally {
      setLoadingMoreTracks(false);
    }
  }, [loadingMoreTracks, tracksExhausted, profileData, user_id]);

  const handlePlayCountIncrement = useCallback(async (trackId) => {
    try {
      await api.post(`/track/${trackId}/play-count`);
    } catch (error) {
      console.error("Failed to increment play count:", error);
    }
  }, []);

  // Phase 3.2: lazy-load secondary tab data
  const loadTabData = async (tab) => {
    if (tabsLoaded[tab]) return;
    try {
      if (tab === 'features') {
        const r = await api.get(`/artist/${user_id}/features`);
        setFeatures(r.data.submissions || []);
        // Phase 20.1 — also load featured beats for the split section
        try {
          const br = await api.get(`/users/${user_id}/beats?featured=true&limit=24`);
          setBeats(br.data?.beats || []);
        } catch (_e) { /* not fatal */ }
      } else if (tab === 'playlists') {
        const r = await api.get(`/artist/${user_id}/playlists`);
        setPlaylists(r.data.playlists || []);
      } else if (tab === 'spotlight') {
        const r = await api.get(`/artist/${user_id}/spotlight-rank`);
        setSpotlightRank(r.data);
      } else if (tab === 'crews') {
        const r = await api.get(`/artist/${user_id}/crews`);
        setCrews({
          created: r.data.created || [],
          member_of: r.data.member_of || [],
          clubs: {
            owned: r.data.clubs?.owned || [],
            subscribed: r.data.clubs?.subscribed || [],
          },
        });
      } else if (tab === 'beats') {
        const r = await api.get(`/users/${user_id}/beats`);
        setBeats(r.data?.beats || []);
      } else if (tab === 'business') {
        // Phase 20.1 — Business Hub profile tab
        const r = await api.get(`/users/${user_id}/businesses`);
        setBusinesses(r.data?.businesses || []);
      }
      setTabsLoaded((prev) => ({ ...prev, [tab]: true }));
    } catch (error) {
      console.error(`Failed to load ${tab} tab:`, error);
    }
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    if (tab !== 'tracks') loadTabData(tab);
    // Reflect tab in URL so deep-links + browser back/share work
    const next = new URLSearchParams(searchParams);
    if (tab === 'tracks') {
      next.delete('tab');
    } else {
      next.set('tab', tab);
    }
    setSearchParams(next, { replace: true });
  };

  const handleDeleteTrack = async (trackId, trackTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${trackTitle}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      await api.delete(`/track/${trackId}`);
      toast.success("Track deleted successfully");
      // Refresh profile to update track list
      fetchProfile();
    } catch (error) {
      console.error("Failed to delete track:", error);
      toast.error("Failed to delete track");
    }
  };

  // Check if current user owns this profile
  const isOwner = currentUser?.user_id === user_id;

  // Phase C #7: Pin tracks (max 6, owner OR collaborator)
  const handlePinToggle = async (track) => {
    const sid = track.submission_id;
    const wasPinned = !!track.is_pinned;
    try {
      if (wasPinned) {
        await api.delete(`/users/me/pinned-tracks/${sid}`);
        toast.success('Unpinned');
      } else {
        await api.post(`/users/me/pinned-tracks/${sid}`);
        toast.success('⭐ Pinned to top');
      }
      // Refresh just the profile (cheaper than full reload)
      const r = await api.get(`/artist/${user_id}`);
      setProfileData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update pin');
    }
  };

  const getDisplayName = () => {
    return profileData?.user?.artist_name || profileData?.user?.name || 'Artist';
  };

  const getLocation = () => {
    const user = profileData?.user;
    if (!user) return null;
    if (user.city && user.country) return `${user.city}, ${user.country}`;
    if (user.city) return user.city;
    if (user.country) return user.country;
    if (user.location) return user.location;
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          <p className="mt-4 text-gray-400">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profileData?.user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <div className="text-center">
          <p className="text-2xl text-white mb-4">Artist not found</p>
          <Link to="/">
            <Button className="bg-purple-600">Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { user, submissions } = profileData;
  const isOwnProfile = currentUser?.user_id === user_id;
  
  // Get media URLs (supports both Cloudinary and local storage)
  const profilePhotoUrl = getProfilePhotoUrl(user, 'thumb');
  const bannerImageUrl = getBannerUrl(user);

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="artist-profile-page">
      {/* Shared Header */}
      <SharedNavigation currentUser={currentUser} />

      {/* Profile Header */}
      <div className="pt-16">
        <div className="relative">
          {/* Banner Image */}
          {user.banner_image ? (
            <div className="h-64 md:h-80 w-full overflow-hidden">
              <img
                src={bannerImageUrl}
                alt="Artist Banner"
                className="w-full h-full object-cover"
                onError={(e) => { e.target.parentElement.classList.add('bg-gradient-to-b', 'from-purple-900/40', 'via-purple-900/20', 'to-transparent'); e.target.style.display = 'none'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-transparent to-transparent"></div>
            </div>
          ) : (
            <div className="h-64 md:h-80 bg-gradient-to-b from-purple-900/40 via-purple-900/20 to-transparent"></div>
          )}
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-24 pb-8">
            <div className="flex flex-col md:flex-row items-start gap-8">
              {/* Profile Photo - Prioritize uploaded photo over Google avatar */}
              <div className="flex-shrink-0">
                <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-purple-500/50 shadow-2xl shadow-purple-500/20 bg-gray-900">
                  <img
                    src={profilePhotoUrl}
                    alt={getDisplayName()}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = getDefaultAvatar(); }}
                  />
                </div>
              </div>

              {/* Profile Info */}
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  {/* iter_194b — Public Presence Status + custom note.
                      Hidden when status is Online / Offline / null or when
                      the update is stale (older than 30 min). */}
                  <PresenceStatusBadge
                    status={user.presence_status}
                    note={user.presence_status_note}
                    updatedAt={user.presence_status_updated_at}
                    size="md"
                    testid="profile-presence-badge"
                  />
                  {/* iter_193 — Public Membership Badges (Admin / Founding Artist / Premium).
                      Reads only public tier data from /api/artist/{id}. */}
                  <MembershipBadges user={user} size="md" testIdPrefix="profile-membership-badge" />
                  {/* Phase 20.1 — Badge System 2.0: only Verified / Founder / Admin
                      render in the profile header. All other badges (referrals,
                      milestones, supporter, etc.) move into the Achievements
                      tab to reduce clutter. */}
                  {user.badges?.filter((b) => ['verified_artist', 'verified', 'founder'].includes(b)).map(badge => (
                    <BadgeWithModal key={badge} type={badge} />
                  ))}
                  {/* Hidden-badge counter chip — explains where the rest moved to */}
                  {(() => {
                    const hidden = (user.badges || []).filter((b) => !['verified_artist', 'verified', 'founder', 'admin', 'founder_artist', 'founding_100'].includes(b)).length
                      + (user.highest_referral_badge ? 1 : 0)
                      + (earningsStats?.is_goat_supporter ? 1 : 0);
                    if (hidden <= 0) return null;
                    return (
                      <button
                        onClick={() => switchTab('achievements')}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-500/15 border border-purple-400/30 text-purple-200 text-xs font-bold hover:bg-purple-500/25"
                        data-testid="profile-badges-overflow"
                        title="View all achievements"
                      >
                        + {hidden} more
                      </button>
                    );
                  })()}
                </div>
                
                <h1 className="text-4xl md:text-5xl font-black text-white mb-2 flex items-center gap-2" data-testid="artist-name">
                  <span>{getDisplayName()}</span>
                  {user.badges?.includes('verified_artist') && (
                    <span
                      title="Verified Artist"
                      className="inline-flex items-center justify-center w-7 h-7 md:w-9 md:h-9 rounded-full bg-blue-500 flex-shrink-0"
                      data-testid="verified-checkmark"
                    >
                      <svg className="w-4 h-4 md:w-5 md:h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                  )}
                  <PresenceDot userId={user_id} size="lg" showLabel className="ml-2" />
                </h1>
                
                {getLocation() && (
                  <p className="text-gray-400 text-lg mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {getLocation()}
                  </p>
                )}

                {user.genre && (
                  <p className="text-purple-400 font-medium mb-4">{user.genre}</p>
                )}

                {user.bio && (
                  <p className="text-gray-300 text-lg max-w-2xl mb-6 leading-relaxed">
                    {user.bio}
                  </p>
                )}

                {/* Phase 15.5 P5 — Joined through attribution */}
                {user.referred_by && user.referrer_name && (
                  <p className="text-sm text-gray-400 mb-4" data-testid="profile-referrer-attribution">
                    <span className="text-gray-500">Joined through </span>
                    <Link
                      to={`/artist/${user.referred_by}`}
                      className="text-purple-300 hover:text-purple-200 font-medium underline-offset-2 hover:underline"
                      data-testid="profile-referrer-link"
                    >
                      {user.referrer_name}
                    </Link>
                  </p>
                )}

                {/* Stats & Follow */}
                <div className="flex items-center flex-wrap gap-x-4 gap-y-2 sm:gap-6 mb-6">
                  <div className="text-center" data-testid="profile-tracks-total">
                    <p className="text-2xl font-bold text-white">{profileData?.total_tracks ?? submissions?.length ?? 0}</p>
                    <p className="text-gray-500 text-sm">Tracks</p>
                  </div>
                  {/* Phase 15.6O — ▶ Total Track Plays (canonical display_plays sum) */}
                  {(() => {
                    const totalPlays = (submissions || []).reduce(
                      (acc, t) => acc + (typeof getDisplayPlays === "function" ? getDisplayPlays(t) : (t?.display_plays ?? t?.play_count ?? 0)),
                      0,
                    );
                    const fmt = typeof formatDisplayPlays === "function"
                      ? formatDisplayPlays(totalPlays)
                      : totalPlays.toLocaleString();
                    return (
                      <div className="text-center" data-testid="profile-total-plays" title={`${totalPlays.toLocaleString()} total plays`}>
                        <p className="text-2xl font-bold text-white">▶ {fmt}</p>
                        <p className="text-gray-500 text-sm">Plays</p>
                      </div>
                    );
                  })()}
                  <button 
                    onClick={() => openFollowersModal('followers')}
                    className="text-center hover:bg-white/5 rounded-lg px-3 py-2 transition-colors cursor-pointer"
                    data-testid="followers-count"
                  >
                    <p className="text-2xl font-bold text-white">{followerCount}</p>
                    <p className="text-gray-500 text-sm hover:text-purple-400 inline-flex items-center gap-1">
                      Followers
                      <MetricInfoIcon metric="followers" />
                    </p>
                  </button>
                  <button 
                    onClick={() => openFollowersModal('following')}
                    className="text-center hover:bg-white/5 rounded-lg px-3 py-2 transition-colors cursor-pointer"
                    data-testid="following-count"
                  >
                    <p className="text-2xl font-bold text-white">{profileData?.user?.following?.length || 0}</p>
                    <p className="text-gray-500 text-sm hover:text-purple-400">Following</p>
                  </button>

                  {/* Phase 7.1 Milestone B — 🎁 Total Gifts Received */}
                  {(earningsStats?.gifts_received_count || 0) > 0 && (
                    <div className="text-center" data-testid="profile-gifts-received-chip" title={`${earningsStats.total_received_coins.toLocaleString()} coins total`}>
                      <p className="text-2xl font-bold text-white">
                        {earningsStats.gifts_received_count}
                      </p>
                      <p className="text-gray-500 text-sm">🎁 Gifts</p>
                    </div>
                  )}

                  {/* Phase 7.1 Milestone B — 🔥 Fan Boosted Tracks */}
                  {(earningsStats?.fan_boosted_track_count || 0) > 0 && (
                    <div className="text-center" data-testid="profile-fan-boosted-chip" title="Tracks with at least one gift">
                      <p className="text-2xl font-bold text-white">
                        {earningsStats.fan_boosted_track_count}
                      </p>
                      <p className="text-gray-500 text-sm">🔥 Fan Boosted</p>
                    </div>
                  )}

                  {/* Phase 4.1: feature + spotlight counts */}
                  {(profileData?.user?.collab_count > 0 || profileData?.user?.feature_count > 0 || profileData?.user?.spotlight_wins > 0 || profileData?.user?.spotlight_appearances > 0) && (
                    <>
                      {profileData?.user?.collab_count > 0 && (
                        <button
                          type="button"
                          onClick={() => switchTab('features')}
                          className="text-center hover:bg-pink-500/10 rounded-lg px-3 py-2 transition-colors cursor-pointer group"
                          title={`See ${profileData.user.collab_count} collab track${profileData.user.collab_count === 1 ? '' : 's'}`}
                          data-testid="profile-collabs-chip"
                        >
                          <p className="text-2xl font-bold text-white group-hover:text-pink-200">
                            {profileData.user.collab_count}
                          </p>
                          <p className="text-gray-500 text-sm group-hover:text-pink-300">
                            🤝 Collabs
                          </p>
                        </button>
                      )}
                      {profileData?.user?.feature_count > 0 && (
                        <div className="text-center" data-testid="feature-count">
                          <p className="text-2xl font-bold text-white">{profileData.user.feature_count}</p>
                          <p className="text-gray-500 text-sm">🔥 Features</p>
                        </div>
                      )}
                      {(profileData?.user?.spotlight_wins > 0 || profileData?.user?.spotlight_appearances > 0) && (
                        <div className="text-center" data-testid="spotlight-wins">
                          <p className="text-2xl font-bold text-white">
                            {profileData.user.spotlight_wins || 0}
                            {profileData.user.spotlight_appearances > profileData.user.spotlight_wins && (
                              <span className="text-base font-normal text-gray-500 ml-1">/ {profileData.user.spotlight_appearances}</span>
                            )}
                          </p>
                          <p className="text-gray-500 text-sm">🏆 Spotlight Wins</p>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Follow Button - Only show if not own profile */}
                  {!isOwnProfile && (
                    <Button
                      onClick={handleFollow}
                      disabled={followLoading}
                      className={`ml-4 ${isFollowing 
                        ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                        : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white'
                      }`}
                      data-testid="follow-button"
                    >
                      {followLoading ? (
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : isFollowing ? (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Following
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Follow
                        </>
                      )}
                    </Button>
                  )}

                  {/* Edit Profile - Only show if own profile */}
                  {isOwnProfile && (
                    <>
                      <Link to="/profile/edit">
                        <Button variant="outline" className="ml-4 border-purple-500/50 text-purple-300 hover:bg-purple-500/20" data-testid="profile-edit-btn">
                          Edit Profile
                        </Button>
                      </Link>
                      <Link to="/business" data-testid="profile-business-hub-link">
                        <Button variant="outline" className="ml-2 border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/20">
                          💼 Business Hub
                        </Button>
                      </Link>
                    </>
                  )}

                  {/* Phase 7 — Send a virtual gift to this artist */}
                  {!isOwnProfile && (
                    <div className="ml-4">
                      <GiftPicker
                        targetType="artist"
                        targetId={user.user_id}
                        recipientId={user.user_id}
                        currentUserId={currentUser?.user_id}
                        currentUserRole={currentUser?.role}
                        onSent={() => setSupportersRefreshKey((k) => k + 1)}
                      />
                    </div>
                  )}

                  {/* Phase 15.6O — Profile actions menu (Share / Report / Block) */}
                  {!isOwnProfile && currentUser?.user_id && (
                    <div className="ml-2">
                      <InviteToStudioButton targetUserId={user.user_id} targetName={user.artist_name || user.name || "this artist"} />
                    </div>
                  )}
                  {!isOwnProfile && currentUser?.user_id && (
                    <div className="ml-2">
                      <ProfileActionsMenu
                        targetUserId={user.user_id}
                        targetName={user.artist_name || user.name || "this artist"}
                      />
                    </div>
                  )}
                  {/* Phase 21.3a4 — Self-profile parity: same Share Profile
                      action + same menu styling on your own profile. */}
                  {isOwnProfile && currentUser?.user_id && (
                    <div className="ml-2" data-testid="profile-actions-self-wrap">
                      <ProfileActionsMenu
                        targetUserId={user.user_id}
                        targetName={user.artist_name || user.name || "your profile"}
                        selfMode
                      />
                    </div>
                  )}
                </div>

                {/* Social Links */}
                <div className="flex flex-wrap gap-3">
                  <SocialLink platform="instagram" url={user.instagram} />
                  <SocialLink platform="tiktok" url={user.tiktok} />
                  <SocialLink platform="youtube" url={user.youtube} />
                  <SocialLink platform="spotify" url={user.spotify} />
                  <SocialLink platform="apple_music" url={user.apple_music} />
                  <SocialLink platform="website" url={user.website} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Phase 7.1 Milestone C — Top Supporters preview + Most Supported
          Track. Public — anyone visiting the profile can see who's
          showing love. */}
      {(earningsStats?.gifts_received_count > 0 || mostSupportedTrack) && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <SupportersPreviewRow
              kind="artist"
              targetId={user_id}
              onViewAll={() => setSupportersOpen(true)}
              refreshKey={supportersRefreshKey}
            />
            {mostSupportedTrack?.submission_id && (
              <Link
                to={`/track/${mostSupportedTrack.submission_id}`}
                className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-rose-500/10 to-pink-500/10 border border-rose-500/30 hover:border-rose-400/60 transition-colors"
                data-testid="profile-most-supported-track"
              >
                <img
                  src={getCoverUrl(mostSupportedTrack)}
                  alt={mostSupportedTrack.track_title || "track"}
                  className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                  onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=120&h=120&fit=crop"; }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-rose-200 text-[10px] uppercase tracking-[0.18em] font-bold">
                    🔥 Most Supported Track
                  </p>
                  <p className="text-white font-semibold truncate">
                    {mostSupportedTrack.track_title || "Untitled"}
                  </p>
                  <p className="text-pink-200/80 text-[11px]">
                    {(mostSupportedTrack.total_coins || 0).toLocaleString()} coins ·{" "}
                    {mostSupportedTrack.gift_count} gift{mostSupportedTrack.gift_count === 1 ? "" : "s"}
                    {mostSupportedTrack.supporter_count > 0 && (
                      <> · {mostSupportedTrack.supporter_count} supporter{mostSupportedTrack.supporter_count === 1 ? "" : "s"}</>
                    )}
                  </p>
                </div>
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Phase 7.1 Milestone C — Top Supporters modal */}
      <TopSupportersModal
        open={supportersOpen}
        onClose={() => setSupportersOpen(false)}
        kind="artist"
        targetId={user_id}
        title={getDisplayName()}
        refreshKey={supportersRefreshKey}
      />

      {/* iter_199 — My Premium Benefits card (OWNER view).
          Live-reads /api/me/tier and shows each unlocked feature with a
          green ✅. Free tier gets an Upgrade CTA; Founding Artist gets a
          Lifetime Founder card (no billing controls); Premium gets
          Manage / Billing.  Non-owner viewers see the concise MembershipBadges
          strip in the header — never this benefits card. */}
      {isOwnProfile && (
        <PremiumBenefitsCard
          founderNumberLabel={user.founder_number_label}
          foundingSinceLabel={user.founding_since_label}
          isAdmin={user.is_admin === true || user.role === "admin"}
        />
      )}
      {/* Non-owner viewers: keep the compact public tier chip block
          (surface the tier + founder identity but no benefits list). */}
      {!isOwnProfile && (user.founder_number || (user.membership_tier && user.membership_tier !== "free")) && (
        <div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4"
          data-testid="profile-membership-section"
        >
          <Card className="p-4 bg-white/5 border-white/10 flex flex-wrap items-center gap-3">
            <MembershipBadges user={user} size="md" testIdPrefix="profile-membership-public-badge" />
            {user.founder_number_label && (
              <span className="text-[11px] text-amber-200/90" data-testid="profile-founder-label">
                ⭐ {user.founder_number_label} · {user.founding_since_label || "Founding 100"}
              </span>
            )}
          </Card>
        </div>
      )}


      {/* Phase 15.6M — Frequent Collaborators rail (auto-hides when empty) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <FrequentCollaborators userId={user_id} />
      </div>

      {/* Phase 21.4 Batch 3D — Contest wins trophy strip. Self-hides
          when the artist has no wins. Always available (independent of
          the Contest Center feature flag — trophies are permanent). */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <ProfileContestWins userId={user_id} />
      </div>

      {/* Canonical RGM Studio Official Hub — inlined DIRECTLY on the
          runtime-resolved official profile (``users.is_official ==
          true``). No dedicated tab or button — the Official
          Community & A&R content lives directly on the account it
          represents. Ordinary artist profiles never render this
          block. Reuses the shared ``OfficialHubRails`` composition
          and the already-resolved ``officialData`` payload so no
          extra network request is issued. */}
      {isOfficialProfile && officialData && (
        <div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8"
          data-testid="rgm-official-hub-inline"
        >
          <OfficialHubRails data={officialData} />
        </div>
      )}

      {/* Phase 3.2: Profile Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="artist-profile-tabs">
        <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-800">
          {[
            { key: 'tracks', label: 'Tracks', count: profileData?.total_tracks ?? submissions?.length ?? 0 },
            { key: 'collaborations', label: '🤝 Collaborations', count: submissions?.filter((s) => Array.isArray(s.featured_artists) && s.featured_artists.length > 0).length || 0 },
            { key: 'features', label: '🎤 Featured On', count: tabsLoaded.features ? features.length : null },
            { key: 'achievements', label: '🏆 Achievements', count: null },
            { key: 'beats', label: '🎹 Beats', count: tabsLoaded.beats ? (beats?.length || 0) : null },
            { key: 'playlists', label: 'Playlists', count: tabsLoaded.playlists ? playlists.length : null },
            { key: 'spotlight', label: 'Spotlight Rank', count: tabsLoaded.spotlight ? (spotlightRank?.count || 0) : null },
            { key: 'crews', label: 'Crews & Clubs', count: tabsLoaded.crews ? (crews.created.length + crews.member_of.length + (crews.clubs?.owned?.length || 0) + (crews.clubs?.subscribed?.length || 0)) : null },
            { key: 'business', label: '💼 Business', count: tabsLoaded.business ? businesses.length : null },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => switchTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-all -mb-px ${
                activeTab === t.key
                  ? 'border-purple-500 text-white'
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              {t.label}{t.count !== null ? <span className="text-gray-500 font-normal ml-1">({t.count})</span> : null}
            </button>
          ))}
        </div>

        {(activeTab === 'tracks' || activeTab === 'collaborations') && (
          <>
            <h2 className="sr-only">{activeTab === 'collaborations' ? 'Collaborations' : 'Music'}</h2>
            {activeTab === 'collaborations' && (
              <p className="text-sm text-purple-200/70 mb-3" data-testid="collaborations-tab-blurb">
                Tracks where {profileData?.user?.artist_name || profileData?.user?.name || "this artist"} features other RGM artists.
              </p>
            )}
            {(() => {
              const visibleTracks = activeTab === 'collaborations'
                ? (submissions || []).filter((s) => Array.isArray(s.featured_artists) && s.featured_artists.length > 0)
                : (submissions || []);
              if (visibleTracks.length === 0) {
                return (
                  <div className="text-center py-12 bg-gray-900/30 rounded-xl border border-gray-800" data-testid={`tab-content-${activeTab}-empty`}>
                    <p className="text-gray-400">
                      {activeTab === 'collaborations' ? 'No collaborations yet — tag featured artists on the upload form to start.' : 'No tracks yet'}
                    </p>
                  </div>
                );
              }
              return (
                <div className="space-y-2" data-testid={`tab-content-${activeTab}`}>
                  {visibleTracks.map((track, index) => (
                    <div
                      key={track.submission_id}
                      className="group bg-gray-900/30 hover:bg-gray-900/60 rounded-lg p-4 transition-all"
                      data-testid={`track-${track.submission_id}`}
                    >
                      <div className="flex items-center gap-4">
                        {/* Track Number */}
                        <div className="w-8 flex-shrink-0 text-center text-gray-500">
                          {track.is_pinned ? (
                            <span className="text-amber-300" title="Pinned" data-testid={`pin-badge-${track.submission_id}`}>⭐</span>
                          ) : (
                            index + 1
                          )}
                        </div>

                        {/* Cover Art */}
                        <div className="w-14 h-14 rounded overflow-hidden flex-shrink-0 relative">
                          <img
                            src={getCoverUrl(track)}
                            alt={track.track_title}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=100&h=100&fit=crop'; }}
                          />
                          <PlayButton
                            track={track}
                            queue={visibleTracks}
                            sourceLabel={`Playing from ${profileData?.user?.artist_name || profileData?.user?.name || "Profile"}`}
                            className="absolute inset-0 m-auto opacity-0 group-hover:opacity-100 transition-opacity"
                            size={36}
                            testIdSuffix="profile-track"
                          />
                        </div>

                        {/* Track Info */}
                        <div className="flex-1 min-w-0">
                          <Link to={`/track/${track.submission_id}`} className="hover:underline">
                            <p className="text-white font-medium truncate">
                              {track.track_title || 'Untitled'}
                            </p>
                          </Link>
                          {Array.isArray(track.featured_artists) && track.featured_artists.length > 0 && (
                            <CollaboratorChips
                              featuredArtists={track.featured_artists}
                              primaryArtistName={profileData?.user?.artist_name}
                              size="sm"
                              maxVisible={3}
                              showWith
                              className="mt-1"
                              testIdSuffix={track.submission_id}
                            />
                          )}
                          {track.genre && <p className="text-gray-500 text-xs truncate mt-0.5">{track.genre}</p>}
                          {/* Singles Store — For-Sale / Preview lock pill row.
                              Only renders for actively-listed singles. */}
                          {isSinglesForSale(track) && (
                            <div className="mt-1">
                              <SinglesLockBadge
                                track={track}
                                variant="row"
                                testIdSuffix={`profile-${track.submission_id}`}
                              />
                            </div>
                          )}
                          {/* Phase 20.1 — Profile Track Performance Metrics */}
                          <div
                            className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-gray-500 mt-1"
                            data-testid={`track-metrics-${track.submission_id}`}
                          >
                            <span title="Total plays" data-testid={`track-plays-${track.submission_id}`}>
                              ▶ {((track.play_count || 0) + (track.promotional_plays || 0)).toLocaleString()}
                            </span>
                            {(track.promotional_plays || 0) > 0 && (
                              <span title="Promotional reach" className="text-amber-300/80"
                                    data-testid={`track-promo-${track.submission_id}`}>
                                📣 +{(track.promotional_plays || 0).toLocaleString()}
                              </span>
                            )}
                            {(track.gifts_received || 0) > 0 && (
                              <span title="Gifts received" className="text-pink-300/80"
                                    data-testid={`track-gifts-${track.submission_id}`}>
                                🎁 {track.gifts_received}
                              </span>
                            )}
                            {(track.playlist_count || 0) > 0 && (
                              <span title="In playlists" className="text-purple-300/80"
                                    data-testid={`track-playlists-${track.submission_id}`}>
                                🎼 {track.playlist_count}
                              </span>
                            )}
                            {(track.shares || track.share_count || 0) > 0 && (
                              <span title="Shares" className="text-blue-300/80"
                                    data-testid={`track-shares-${track.submission_id}`}>
                                ↗ {track.shares || track.share_count}
                              </span>
                            )}
                            {track.status === 'featured' && (
                              <span className="text-yellow-300/80" title="Featured" data-testid={`track-status-feat-${track.submission_id}`}>
                                🎖️ Featured
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Featured Badge */}
                        {track.status === 'featured' && (
                          <span className="px-2 py-1 bg-gradient-to-r from-yellow-400 to-amber-500 text-black text-xs font-bold rounded-full flex-shrink-0 flex items-center gap-1" data-testid={`track-featured-badge-${track.submission_id}`}>
                            🎖️ Featured
                          </span>
                        )}

                        {/* Collab Badge */}
                        {Array.isArray(track.featured_artists) && track.featured_artists.length > 0 && (
                          <span
                            className="px-2 py-1 bg-pink-500/20 text-pink-200 border border-pink-500/30 text-xs font-bold rounded-full flex-shrink-0"
                            title={`Collaboration with ${track.featured_artists.map((f) => f.artist_name || f.name).filter(Boolean).join(', ')}`}
                            data-testid={`track-collab-badge-${track.submission_id}`}
                          >
                            🤝 Collab
                          </span>
                        )}

                        {/* Phase 16.0 — Track actions kebab menu.
                            Owner / admin / any logged-in user gets a tailored
                            set of actions (Edit, Add to Playlist, Share to
                            Community, Share, Pin/Unpin, Delete). Avoids the
                            "actions vanished after mini-player refactor"
                            complaint by surfacing every owner action right on
                            the row, on mobile and desktop. */}
                        {currentUser && (
                          <TrackActionsMenu
                            track={track}
                            currentUser={currentUser}
                            isOwner={isOwnProfile || track.user_id === currentUser?.user_id}
                            isAdmin={currentUser?.role === "admin"}
                            onPinToggle={handlePinToggle}
                            onDelete={(t) => handleDeleteTrack(t.submission_id, t.track_title || "this track")}
                            onShareCommunity={(t) => setShareCommunityTrack(t)}
                            shareBaseUrl={BACKEND_URL}
                            testIdPrefix="profile-track-actions"
                            align="right"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                  {activeTab === 'tracks' && !tracksExhausted && (profileData?.has_more_tracks || ((profileData?.total_tracks || 0) > (submissions?.length || 0))) && (
                    <div className="pt-4 flex justify-center" data-testid="load-more-tracks-wrap">
                      <Button
                        onClick={handleLoadMoreTracks}
                        disabled={loadingMoreTracks}
                        variant="outline"
                        className="border-purple-500/40 text-purple-200 hover:bg-purple-500/10"
                        data-testid="load-more-tracks-btn"
                      >
                        {loadingMoreTracks ? "Loading…" : `Load more (${Math.max(0, (profileData?.total_tracks || 0) - (submissions?.length || 0))} left)`}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        )}

        {/* Phase 15.5 P1 — Beats Tab */}
        {activeTab === 'beats' && (
          <div data-testid="tab-content-beats">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="text-sm text-gray-400">
                Beats from this producer · {beats.length} total
              </p>
              {currentUser?.user_id === user_id && (
                <a
                  href="/submit-beat"
                  className="text-sm font-bold px-3 py-1.5 rounded-md bg-gradient-to-r from-pink-600 to-purple-600 text-white hover:opacity-90"
                  data-testid="profile-upload-beat-btn"
                >
                  + Upload beat
                </a>
              )}
            </div>
            {beats.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {beats.map((b) => (
                  <BeatCard
                    key={b.beat_id}
                    beat={{ ...b, producer: user }}
                    onContact={setContactBeat}
                    currentUserId={currentUser?.user_id}
                    onHidden={(bid) => setBeats((prev) => prev.filter((x) => x.beat_id !== bid))}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-900/30 rounded-xl border border-gray-800">
                <div className="text-4xl mb-2">🎹</div>
                <p className="text-gray-400">No beats yet</p>
                {currentUser?.user_id === user_id && (
                  <p className="text-xs text-gray-500 mt-1">Tap "Upload beat" to publish your first.</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Features Tab — Phase 20.1: split into Featured Tracks + Featured Beats */}
        {activeTab === 'features' && (
          <div data-testid="tab-content-features" className="space-y-8">
            {/* Featured Tracks (Artist side) */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-white font-bold text-lg">🎤 Featured Tracks</h3>
                <span className="text-xs text-gray-500">Artist features &amp; collabs</span>
              </div>
              {features.length > 0 ? (
                <ShowMoreGrid
                  items={features}
                  initial={6}
                  testIdPrefix="featured-tracks"
                  gridClassName="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                >
                  {(track) => {
                  const primaryName =
                    track.primary_artist?.artist_name
                    || track.primary_artist?.name
                    || track.artist_name
                    || "—";
                  // "Featured with" — other collaborators on the same track,
                  // excluding the currently viewed artist.
                  const others = (track.featured_artists || [])
                    .filter((f) => f.user_id && f.user_id !== user_id)
                    .map((f) => f.artist_name || f.name)
                    .filter(Boolean);
                  // 2+ collaborators total → call it a "Collab" (mutual feature).
                  const totalCollaborators = (track.featured_artists || []).length;
                  const isCollab = totalCollaborators >= 2;
                  const isSpotlightFeatured = track.status === 'featured';
                  return (
                    <Link
                      key={track.submission_id}
                      to={`/track/${track.submission_id}`}
                      className="bg-gray-900/40 hover:bg-gray-900/70 border border-gray-800 rounded-xl overflow-hidden transition-all"
                      data-testid={`feature-${track.submission_id}`}
                    >
                      <div className="aspect-square bg-gray-800">
                        <img src={getCoverUrl(track)} alt={track.track_title} className="w-full h-full object-cover" />
                      </div>
                      <div className="p-3">
                        <p className="text-white font-medium truncate">{track.track_title || 'Untitled'}</p>
                        <p className="text-gray-400 text-sm truncate">
                          by {primaryName}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-1">
                          <span
                            className={`inline-block px-2 py-0.5 text-xs rounded-full ${
                              isCollab
                                ? "bg-pink-500/20 text-pink-300"
                                : "bg-purple-500/20 text-purple-300"
                            }`}
                            data-testid={`feature-label-${track.submission_id}`}
                          >
                            {isCollab ? "🤝 Collab" : "🎤 Feat."}
                          </span>
                          {isSpotlightFeatured && (
                            <span
                              className="inline-block px-2 py-0.5 bg-amber-500/20 text-amber-200 text-[11px] rounded-full border border-amber-500/30"
                              data-testid={`feature-spotlight-${track.submission_id}`}
                              title="RGM Featured track"
                            >
                              🎖️ Featured
                            </span>
                          )}
                          {others.length > 0 && (
                            <span
                              className="inline-block px-2 py-0.5 bg-pink-500/10 text-pink-300 text-[11px] rounded-full truncate max-w-[180px]"
                              title={`With ${others.join(', ')}`}
                              data-testid={`feature-with-${track.submission_id}`}
                            >
                              with {others.slice(0, 2).join(', ')}{others.length > 2 ? `, +${others.length - 2}` : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                }}
              </ShowMoreGrid>
              ) : (
                <div className="text-center py-10 bg-gray-900/30 rounded-xl border border-gray-800">
                  <p className="text-gray-400">No featured tracks yet</p>
                  <p className="text-gray-600 text-sm mt-1">Tracks tagging this artist as a collaborator will appear here.</p>
                </div>
              )}
            </section>

            {/* Phase 20.1 — Featured Beats (Producer side, kept separate from Artist features) */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-white font-bold text-lg">🎹 Featured Beats</h3>
                <span className="text-xs text-gray-500">Producer features</span>
              </div>
              {(beats || []).filter((b) => b.featured).length > 0 ? (
                <ShowMoreGrid
                  items={beats.filter((b) => b.featured)}
                  initial={8}
                  testIdPrefix="featured-beats"
                  gridClassName="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4"
                >
                  {(b) => (
                    <Link
                      key={b.beat_id}
                      to={`/beats/${b.beat_id}`}
                      className="bg-gray-900/40 hover:bg-gray-900/70 border border-gray-800 hover:border-pink-500/40 rounded-xl overflow-hidden transition-all block"
                      data-testid={`featured-beat-${b.beat_id}`}
                    >
                      <div className="aspect-square bg-gray-800 relative">
                        {b.cover_url && (
                          <img src={optimizeImage(b.cover_url, 'card')} alt={b.title} className="w-full h-full object-cover" loading="lazy" />
                        )}
                        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-500 to-pink-500 text-black text-[10px] font-black uppercase">
                          ⭐ Featured Beat
                        </span>
                        {(b.gifts_received || 0) > 0 && (
                          <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-black/80 text-amber-200 text-[10px] font-bold border border-amber-400/30"
                                data-testid={`featured-beat-gifts-${b.beat_id}`}>
                            🎁 {b.gifts_received}
                          </span>
                        )}
                      </div>
                      <div className="p-3">
                        <p className="text-white font-medium truncate">{b.title}</p>
                        <p className="text-gray-500 text-xs">
                          {b.bpm ? `${b.bpm} BPM` : ''}{b.genre ? ` · ${b.genre}` : ''}
                        </p>
                      </div>
                    </Link>
                  )}
                </ShowMoreGrid>
              ) : (
                <div className="text-center py-10 bg-gray-900/30 rounded-xl border border-gray-800">
                  <p className="text-gray-400">No featured beats yet</p>
                  <p className="text-gray-600 text-sm mt-1">Featured Beats picked by RGM will appear here.</p>
                </div>
              )}
            </section>
          </div>
        )}

        {/* Playlists Tab */}
        {activeTab === 'playlists' && (
          <div data-testid="tab-content-playlists">
            {playlists.length > 0 ? (
              <ShowMoreGrid
                items={playlists}
                initial={8}
                testIdPrefix="playlists"
                gridClassName="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4"
              >
                {(pl) => (
                  <Link
                    key={pl.playlist_id}
                    to={`/playlist/${pl.playlist_id}`}
                    className="bg-gray-900/40 hover:bg-gray-900/70 border border-gray-800 rounded-xl overflow-hidden transition-all"
                    data-testid={`playlist-${pl.playlist_id}`}
                  >
                    <div className="aspect-square bg-gradient-to-br from-purple-700/40 to-pink-700/40 flex items-center justify-center">
                      {pl.cover_image ? (
                        <img src={getPlaylistCoverUrl(pl)} alt={pl.name} className="w-full h-full object-cover" />
                      ) : (
                        <svg className="w-12 h-12 text-purple-300" fill="currentColor" viewBox="0 0 20 20"><path d="M3 3h14a1 1 0 011 1v3H2V4a1 1 0 011-1zm-1 6h16v8a1 1 0 01-1 1H3a1 1 0 01-1-1V9z" /></svg>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-white font-medium truncate">{pl.name}</p>
                      <p className="text-gray-500 text-xs">{pl.track_count} tracks · {pl.total_plays_gained} plays gained</p>
                    </div>
                  </Link>
                )}
              </ShowMoreGrid>
            ) : (
              <div className="text-center py-12 bg-gray-900/30 rounded-xl border border-gray-800">
                <p className="text-gray-400">No public playlists yet</p>
              </div>
            )}
          </div>
        )}

        {/* Spotlight Rank Tab */}
        {activeTab === 'spotlight' && (
          <div data-testid="tab-content-spotlight">
            {spotlightRank && spotlightRank.count > 0 ? (
              <>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-yellow-500/20 to-amber-700/10 border border-yellow-500/30 rounded-xl p-4 text-center">
                    <p className="text-yellow-300 text-xs uppercase tracking-wider">Best Position</p>
                    <p className="text-3xl font-bold text-white mt-1">
                      {spotlightRank.best_position ? `#${spotlightRank.best_position}` : '—'}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-500/20 to-pink-700/10 border border-purple-500/30 rounded-xl p-4 text-center">
                    <p className="text-purple-300 text-xs uppercase tracking-wider">Appearances</p>
                    <p className="text-3xl font-bold text-white mt-1">{spotlightRank.count}</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-500/20 to-emerald-700/10 border border-green-500/30 rounded-xl p-4 text-center">
                    <p className="text-green-300 text-xs uppercase tracking-wider">#1 Wins</p>
                    <p className="text-3xl font-bold text-white mt-1">{spotlightRank.wins}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {spotlightRank.appearances.map((a, idx) => (
                    <Link
                      key={`${a.spotlight_id}-${idx}`}
                      to={`/track/${a.submission_id}`}
                      className="flex items-center gap-4 bg-gray-900/40 hover:bg-gray-900/70 border border-gray-800 rounded-lg p-3 transition-all"
                    >
                      <span className="w-8 text-center text-yellow-400 font-bold">
                        {a.position === 1 ? '🥇' : a.position === 2 ? '🥈' : a.position === 3 ? '🥉' : `#${a.position}`}
                      </span>
                      <img src={getCoverUrl(a)} alt={a.track_title} className="w-12 h-12 rounded object-cover" />
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{a.track_title || 'Untitled'}</p>
                        <p className="text-gray-500 text-xs">{a.week_label}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12 bg-gray-900/30 rounded-xl border border-gray-800">
                <p className="text-gray-400">No spotlight appearances yet</p>
                <p className="text-gray-600 text-sm mt-1">When this artist's tracks are spotlighted, they'll appear here.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'achievements' && (
          <div data-testid="tab-content-achievements">
            <AchievementsTab userId={user_id} />
          </div>
        )}

        {activeTab === 'crews' && (
          <div data-testid="tab-content-crews">
            {(crews.created.length === 0 && crews.member_of.length === 0
              && (crews.clubs?.owned?.length || 0) === 0
              && (crews.clubs?.subscribed?.length || 0) === 0) ? (
              <div className="text-center py-12 bg-gray-900/30 rounded-xl border border-gray-800">
                <p className="text-3xl mb-2">🤝</p>
                <p className="text-gray-400">Not in any crews or clubs yet</p>
                <p className="text-gray-600 text-sm mt-1">When this artist creates or joins a crew or launches a club, it'll appear here.</p>
                <Link to="/crews">
                  <button className="mt-4 px-4 py-2 rounded-md bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-bold">
                    Discover Crews & Clubs
                  </button>
                </Link>
              </div>
            ) : (
              <div className="space-y-8">
                {crews.created.length > 0 && (
                  <section>
                    <h3 className="text-white font-bold text-lg mb-3">✨ Created</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {crews.created.map((c) => (
                        <Link
                          key={c.crew_id}
                          to={`/crews/${c.crew_id}`}
                          className="group bg-gray-900/50 hover:bg-gray-900/80 border border-purple-500/20 hover:border-purple-500/60 rounded-xl overflow-hidden transition-all"
                          data-testid={`profile-crew-${c.crew_id}`}
                        >
                          <div className="aspect-[16/9] bg-gradient-to-br from-purple-900/40 to-pink-900/40 overflow-hidden">
                            {c.cover_image && (
                              <img
                                src={getPlaylistCoverUrl(c)}
                                alt={c.name}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1614149162883-504ce4d13909?w=600&h=340&fit=crop'; }}
                              />
                            )}
                          </div>
                          <div className="p-3">
                            <p className="text-white font-medium truncate">{c.name}</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              👥 {c.member_count} · {c.kind === 'group' ? 'Group' : c.kind === 'club' ? 'Club' : 'Crew'}
                              {c.visibility === 'private' && ' · 🔒'}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </section>
                )}
                {crews.member_of.length > 0 && (
                  <section>
                    <h3 className="text-white font-bold text-lg mb-3">Member of</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {crews.member_of.map((c) => (
                        <Link
                          key={c.crew_id}
                          to={`/crews/${c.crew_id}`}
                          className="group bg-gray-900/50 hover:bg-gray-900/80 border border-gray-800 hover:border-purple-500/60 rounded-xl overflow-hidden transition-all"
                          data-testid={`profile-crew-${c.crew_id}`}
                        >
                          <div className="aspect-[16/9] bg-gradient-to-br from-purple-900/40 to-pink-900/40 overflow-hidden">
                            {c.cover_image && (
                              <img
                                src={getPlaylistCoverUrl(c)}
                                alt={c.name}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1614149162883-504ce4d13909?w=600&h=340&fit=crop'; }}
                              />
                            )}
                          </div>
                          <div className="p-3">
                            <p className="text-white font-medium truncate">{c.name}</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              👥 {c.member_count} · {c.kind === 'group' ? 'Group' : c.kind === 'club' ? 'Club' : 'Crew'}
                              {c.visibility === 'private' && ' · 🔒'}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </section>
                )}
                {(crews.clubs?.owned?.length > 0) && (
                  <section data-testid="profile-clubs-owned">
                    <h3 className="text-white font-bold text-lg mb-3 flex items-center gap-2">
                      <span>🎟️</span> Club <span className="text-amber-300 text-sm font-normal">(Owner)</span>
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {crews.clubs.owned.map((c) => (
                        <Link
                          key={c.club_id}
                          to={`/clubs/${c.club_id}`}
                          className="group bg-gray-900/50 hover:bg-gray-900/80 border border-amber-500/20 hover:border-amber-500/60 rounded-xl overflow-hidden transition-all"
                          data-testid={`profile-club-${c.club_id}`}
                        >
                          <div className="aspect-[16/9] bg-gradient-to-br from-amber-900/40 to-orange-900/40 overflow-hidden">
                            {c.cover_image && (
                              <img
                                src={getPlaylistCoverUrl(c)}
                                alt={c.name}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1614149162883-504ce4d13909?w=600&h=340&fit=crop'; }}
                              />
                            )}
                          </div>
                          <div className="p-3">
                            <p className="text-white font-medium truncate">{c.name}</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              ⭐ {c.subscriber_count || 0} · Club
                              {(c.price_monthly && c.price_monthly > 0)
                                ? ` · $${Number(c.price_monthly).toFixed(2)}/mo`
                                : ' · Free'}
                              {c.visibility === 'private' && ' · 🔒'}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </section>
                )}
                {(crews.clubs?.subscribed?.length > 0) && (
                  <section data-testid="profile-clubs-subscribed">
                    <h3 className="text-white font-bold text-lg mb-3 flex items-center gap-2">
                      <span>🎟️</span> Subscribed Clubs
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {crews.clubs.subscribed.map((c) => (
                        <Link
                          key={c.club_id}
                          to={`/clubs/${c.club_id}`}
                          className="group bg-gray-900/50 hover:bg-gray-900/80 border border-gray-800 hover:border-amber-500/60 rounded-xl overflow-hidden transition-all"
                          data-testid={`profile-club-${c.club_id}`}
                        >
                          <div className="aspect-[16/9] bg-gradient-to-br from-amber-900/40 to-orange-900/40 overflow-hidden">
                            {c.cover_image && (
                              <img
                                src={getPlaylistCoverUrl(c)}
                                alt={c.name}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1614149162883-504ce4d13909?w=600&h=340&fit=crop'; }}
                              />
                            )}
                          </div>
                          <div className="p-3">
                            <p className="text-white font-medium truncate">{c.name}</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              ⭐ {c.subscriber_count || 0} · Club
                              {(c.price_monthly && c.price_monthly > 0)
                                ? ` · $${Number(c.price_monthly).toFixed(2)}/mo`
                                : ' · Free'}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </div>
        )}

        {/* Phase 20.1 — Business Hub profile tab */}
        {activeTab === 'business' && (
          <div data-testid="tab-content-business">
            {businesses.length === 0 ? (
              <div className="text-center py-12 bg-gray-900/30 rounded-xl border border-gray-800">
                <p className="text-3xl mb-2">💼</p>
                <p className="text-gray-400">No business listings yet</p>
                <p className="text-gray-600 text-sm mt-1">
                  When this artist publishes a Business Hub listing, it'll appear here.
                </p>
                <Link to="/business-hub">
                  <button className="mt-4 px-4 py-2 rounded-md bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-bold"
                          data-testid="business-tab-discover-cta">
                    Browse Business Hub
                  </button>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {businesses.map((b) => (
                  <Link
                    key={b.business_id}
                    to={`/business/${b.business_id}`}
                    className="group bg-gray-900/50 hover:bg-gray-900/80 border border-purple-500/20 hover:border-purple-500/60 rounded-xl overflow-hidden transition-all"
                    data-testid={`profile-business-${b.business_id}`}
                  >
                    <div className="aspect-[16/9] bg-gradient-to-br from-amber-900/40 to-pink-900/40 overflow-hidden">
                      {(b.cover_image || b.logo) && (
                        <img src={optimizeImage(b.cover_image || b.logo, 'card')}
                             alt={b.name}
                             className="w-full h-full object-cover"
                             onError={(e) => { e.target.style.display = 'none'; }} />
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-white font-bold truncate flex items-center gap-1">
                        {b.name}
                        {b.verified && <span className="text-emerald-300 text-xs">✓</span>}
                      </p>
                      {b.tagline && (
                        <p className="text-xs text-gray-400 truncate">{b.tagline}</p>
                      )}
                      <p className="text-[11px] text-gray-500 mt-1">
                        {[b.category, b.city, b.country].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Suggested Artists Section */}
      {suggestedArtists.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Similar Artists
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {suggestedArtists.map(artist => (
              <Link 
                key={artist.user_id} 
                to={`/artist/${artist.user_id}`}
                className="group"
              >
                <div className="bg-gray-900/50 rounded-xl p-4 hover:bg-gray-900/80 transition-all text-center">
                  <div className="w-20 h-20 mx-auto rounded-full overflow-hidden border-2 border-purple-500/30 mb-3">
                    <img
                      src={getProfilePhotoUrl(artist)}
                      alt={artist.artist_name || artist.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100&h=100&fit=crop'; }}
                    />
                  </div>
                  <h4 className="font-medium text-white truncate group-hover:text-purple-400 transition-colors">
                    {artist.artist_name || artist.name}
                  </h4>
                  <p className="text-gray-500 text-sm">{artist.follower_count || 0} followers</p>
                  <div className="mt-1 flex items-center justify-center gap-1.5 flex-wrap">
                    {artist.collab_count > 0 && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-pink-500/15 text-pink-200 border border-pink-500/30 text-[10px] font-semibold"
                        title={`${artist.collab_count} collaboration${artist.collab_count === 1 ? '' : 's'}`}
                        data-testid={`similar-artist-collab-${artist.user_id}`}
                      >
                        🤝 {artist.collab_count}
                      </span>
                    )}
                    {artist.badges?.includes('producer') && (
                      <span className="inline-block px-2 py-0.5 bg-blue-600/30 text-blue-300 text-xs rounded-full">
                        Producer
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-gray-800 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/" className="inline-flex items-center justify-center">
            <img src="/rgm-logo.png" alt="RGM Studio" className="h-8 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity" />
          </Link>
        </div>
      </footer>

      {/* Followers/Following Modal */}
      {showFollowersModal && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setShowFollowersModal(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Followers and Following"
          data-testid="followers-following-modal"
        >
          <div 
            className="bg-[#1a1a1e] border border-purple-900/30 rounded-xl w-full max-w-md max-h-[70vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-purple-900/30">
              <div className="flex gap-4">
                <button
                  onClick={() => { setFollowersModalTab('followers'); fetchFollowersData('followers'); }}
                  className={`font-medium transition-colors ${
                    followersModalTab === 'followers' 
                      ? 'text-white border-b-2 border-purple-500 pb-1' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Followers
                </button>
                <button
                  onClick={() => { setFollowersModalTab('following'); fetchFollowersData('following'); }}
                  className={`font-medium transition-colors ${
                    followersModalTab === 'following' 
                      ? 'text-white border-b-2 border-purple-500 pb-1' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Following
                </button>
              </div>
              <button 
                onClick={() => setShowFollowersModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="overflow-y-auto max-h-[calc(70vh-60px)]">
              {loadingFollowers ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
                </div>
              ) : (
                <div className="p-2">
                  {(followersModalTab === 'followers' ? followersList : followingList).length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      <p>{followersModalTab === 'followers' ? 'No followers yet' : 'Not following anyone yet'}</p>
                    </div>
                  ) : (
                    (followersModalTab === 'followers' ? followersList : followingList).map((person) => (
                      <Link
                        key={person.user_id}
                        to={`/artist/${person.user_id}`}
                        onClick={() => setShowFollowersModal(false)}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-purple-900/20 transition-colors"
                      >
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 overflow-hidden flex-shrink-0">
                          <img
                            src={getProfilePhotoUrl(person)}
                            alt={person.artist_name || person.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white truncate flex items-center gap-1.5">
                            <span className="truncate">{person.artist_name || person.name}</span>
                            <PresenceDot userId={person.user_id} size="sm" />
                          </p>
                          {person.badges && person.badges.length > 0 && (
                            <div className="flex gap-1 mt-1">
                              {evolveBadges(person.badges).slice(0, 2).map((badge) => (
                                <BadgeWithModal key={badge} type={badge} size="small" />
                              ))}
                            </div>
                          )}
                        </div>
                      </Link>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Phase 15.5 P1 — Contact producer dialog */}
      <ContactProducerDialog
        open={!!contactBeat}
        onOpenChange={(o) => !o && setContactBeat(null)}
        beat={contactBeat}
      />

      {/* Phase 16.0 — Share-to-community dialog (driven by TrackActionsMenu). */}
      <ShareToCommunityDialog
        open={!!shareCommunityTrack}
        onClose={() => setShareCommunityTrack(null)}
        submissionId={shareCommunityTrack?.submission_id}
        trackTitle={shareCommunityTrack?.track_title}
      />
    </div>
  );
};

export default ArtistProfilePage;
